import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface CertidaoPadDialogData {
  dataConsulta: Date;
  cpf: string;
  nome: string;
  situacao: 'nada_consta' | 'possui_pendencias';
  servidorId?: number;
  podeEmitir: boolean;
  mensagemPendencias?: string;
  /** Administrador + CGE: emissão completa */
  emissaoCompleta?: boolean;
  processosSispac?: ProcessoAtivo[];
  processosLegado?: ProcessoLegado[];
}

export interface ProcessoAtivo {
  id: number;
  identificador_formatado: string;
  data_extrato_portaria: string;
  status: string;
  fase_atual: string;
  orgao: { nome: string };
}

export interface ProcessoLegado {
  numr_processo: string;
  orgao: string;
  status: string;
  prescricao_situacao: string;
  prescricao_dias: number;
  prescricao_expirada: boolean;
}

export interface ProcessoUnificado {
  numero: string;
  orgao: string;
  status: string;
  detalhe: string;
}

@Component({
  selector: 'app-certidao-pad-dialog',
  templateUrl: './certidao-pad-dialog.component.html',
  styleUrls: ['./certidao-pad-dialog.component.css']
})
export class CertidaoPadDialogComponent implements OnInit {
  processosAtivos: ProcessoAtivo[] = [];
  processosLegado: ProcessoLegado[] = [];
  processosUnificados: ProcessoUnificado[] = [];
  processosSelecionados = new Set<string>();
  observacoes = '';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: CertidaoPadDialogData,
    private dialogRef: MatDialogRef<CertidaoPadDialogComponent>
  ) {}

  get situacaoLabel(): string {
    return this.data.situacao === 'nada_consta' ? 'Nada Consta' : 'Possui Pendências';
  }

  get dataConsultaFormatada(): string {
    const d = this.data.dataConsulta;
    return d ? new Date(d).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }) : '';
  }

  get consultadoLabel(): string {
    const cpf = this.data.cpf || '';
    const nome = this.data.nome || '';
    return `${cpf} - ${nome}`.trim();
  }

  get emissaoCompleta(): boolean {
    return !!this.data.emissaoCompleta;
  }

  /** Tabela de processos apenas para emissão completa (adm + CGE) */
  get showProcessTable(): boolean {
    return this.emissaoCompleta && this.processosUnificados.length > 0;
  }

  get showAdminExtras(): boolean {
    return this.emissaoCompleta && this.data.situacao === 'possui_pendencias';
  }

  get podeMostrarBotaoEmitir(): boolean {
    if (!this.data.podeEmitir) {
      return false;
    }
    if (this.emissaoCompleta) {
      return !this.showProcessTable || this.algumSelecionado;
    }
    return this.data.situacao === 'nada_consta';
  }

  get todosSelecionados(): boolean {
    return this.processosUnificados.length > 0 &&
      this.processosUnificados.every(p => this.processosSelecionados.has(p.numero));
  }

  get algumSelecionado(): boolean {
    return this.processosUnificados.some(p => this.processosSelecionados.has(p.numero));
  }

  ngOnInit(): void {
    this.processosAtivos = this.data.processosSispac || [];
    this.processosLegado = this.data.processosLegado || [];
    this.processosUnificados = [
      ...this.processosLegado.map((p) => ({
        numero: p.numr_processo,
        orgao: p.orgao || '-',
        status: p.status || '-',
        detalhe: p.prescricao_situacao || '-',
      })),
      ...this.processosAtivos.map((p) => ({
        numero: p.identificador_formatado,
        orgao: p.orgao?.nome || '-',
        status: p.status === 'concluido' ? 'Concluído' : 'Em andamento',
        detalhe: p.fase_atual || '-',
      })),
    ];
    if (this.emissaoCompleta) {
      this.processosUnificados.forEach(p => this.processosSelecionados.add(p.numero));
    }
  }

  toggleProcesso(numero: string): void {
    if (this.processosSelecionados.has(numero)) {
      this.processosSelecionados.delete(numero);
    } else {
      this.processosSelecionados.add(numero);
    }
  }

  toggleTodos(): void {
    if (this.todosSelecionados) {
      this.processosSelecionados.clear();
    } else {
      this.processosUnificados.forEach(p => this.processosSelecionados.add(p.numero));
    }
  }

  emitirESair(): void {
    this.dialogRef.close({
      emitir: true,
      processosSelecionados: [...this.processosSelecionados],
      observacoes: this.observacoes ? this.observacoes.trim() : undefined
    });
  }

  emitirNadaConsta(): void {
    this.dialogRef.close({
      emitir: true,
      forcarNadaConsta: true,
      processosSelecionados: [...this.processosSelecionados],
      observacoes: this.observacoes ? this.observacoes.trim() : undefined
    });
  }

  sair(): void {
    this.dialogRef.close({ emitir: false });
  }
}
